import React, { useState, useEffect, useCallback } from 'react';
import { Bug, Lightbulb, MessageCircle, Heart, CreditCard, Compass, Eye, Clock, CheckCircle2, XCircle, Loader2, ChevronDown, Image as ImageIcon } from 'lucide-react';
import { getAuthHeaders } from '../context/AuthContext';
import AdminLayout from '../components/AdminLayout';
import { resolveImageUrl } from '../utils/imageUrl';

import { BACKEND_URL } from '../config/api';

const STATUS_CONFIG = {
  new: { label: 'New', color: 'bg-blue-100 text-blue-700', icon: Clock },
  reviewed: { label: 'Reviewed', color: 'bg-purple-100 text-purple-700', icon: Eye },
  in_progress: { label: 'In Progress', color: 'bg-amber-100 text-amber-700', icon: Loader2 },
  resolved: { label: 'Resolved', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  wont_fix: { label: "Won't Fix", color: 'bg-gray-100 text-gray-600', icon: XCircle },
};

const TYPE_CONFIG = {
  bug: { label: 'Bug', icon: Bug, color: 'text-red-500' },
  feature: { label: 'Feature', icon: Lightbulb, color: 'text-amber-500' },
  billing: { label: 'Billing', icon: CreditCard, color: 'text-green-500' },
  usability: { label: 'Usability', icon: Compass, color: 'text-indigo-500' },
  general: { label: 'General', icon: MessageCircle, color: 'text-blue-500' },
  praise: { label: 'Praise', icon: Heart, color: 'text-pink-500' },
};

const AdminFeedback = () => {
  const [feedback, setFeedback] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [expandedItem, setExpandedItem] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  const fetchFeedback = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      if (filterType) params.set('feedback_type', filterType);
      params.set('limit', '100');

      const res = await fetch(`${BACKEND_URL}/api/admin/feedback?${params}`, {
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.clone().json();
        setFeedback(data.items || []);
        setTotal(data.total || 0);
      }
    } catch (err) {
      console.error('Fetch feedback error:', err);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterType]);

  useEffect(() => {
    fetchFeedback();
  }, [fetchFeedback]);

  const updateStatus = async (feedbackId, newStatus, adminNotes) => {
    setUpdatingId(feedbackId);
    try {
      const body = { status: newStatus };
      if (adminNotes !== undefined) body.admin_notes = adminNotes;

      const res = await fetch(`${BACKEND_URL}/api/admin/feedback/${feedbackId}/status`, {
        method: 'PUT',
        credentials: 'include',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setFeedback(prev => prev.map(f =>
          f.feedback_id === feedbackId ? { ...f, status: newStatus, ...(adminNotes !== undefined ? { admin_notes: adminNotes } : {}) } : f
        ));
      }
    } catch (err) {
      console.error('Update feedback error:', err);
    } finally {
      setUpdatingId(null);
    }
  };

  const statusCounts = feedback.reduce((acc, f) => {
    acc[f.status] = (acc[f.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-light text-charcoal">User Feedback</h1>
          <p className="text-sm text-gray-soft mt-1">{total} submissions</p>
        </div>

        {/* Status summary pills */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilterStatus('')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              !filterStatus ? 'bg-charcoal text-white' : 'bg-white text-charcoal border border-beige hover:border-charcoal'
            }`}
            data-testid="filter-all"
          >
            All ({total})
          </button>
          {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
            <button
              key={key}
              onClick={() => setFilterStatus(filterStatus === key ? '' : key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                filterStatus === key ? 'bg-charcoal text-white' : `${cfg.color} hover:opacity-80`
              }`}
              data-testid={`filter-status-${key}`}
            >
              {cfg.label} ({statusCounts[key] || 0})
            </button>
          ))}
        </div>

        {/* Type filter */}
        <div className="flex gap-2">
          {Object.entries(TYPE_CONFIG).map(([key, cfg]) => {
            const Icon = cfg.icon;
            return (
              <button
                key={key}
                onClick={() => setFilterType(filterType === key ? '' : key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                  filterType === key ? 'bg-charcoal text-white border-charcoal' : 'bg-white text-charcoal border-beige hover:border-charcoal'
                }`}
                data-testid={`filter-type-${key}`}
              >
                <Icon size={12} />
                {cfg.label}
              </button>
            );
          })}
        </div>

        {/* Feedback list */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-sage" size={32} />
          </div>
        ) : feedback.length === 0 ? (
          <div className="text-center py-12 text-gray-soft">
            <MessageCircle size={40} className="mx-auto mb-3 opacity-30" />
            <p>No feedback yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {feedback.map((item) => {
              const typeConfig = TYPE_CONFIG[item.feedback_type] || TYPE_CONFIG.general;
              const statusConfig = STATUS_CONFIG[item.status] || STATUS_CONFIG.new;
              const TypeIcon = typeConfig.icon;
              const StatusIcon = statusConfig.icon;
              const isExpanded = expandedItem === item.feedback_id;

              return (
                <div
                  key={item.feedback_id}
                  className="bg-white rounded-xl border border-beige overflow-hidden"
                  data-testid={`feedback-item-${item.feedback_id}`}
                >
                  {/* Header row */}
                  <button
                    onClick={() => setExpandedItem(isExpanded ? null : item.feedback_id)}
                    className="w-full p-4 flex items-start gap-3 text-left hover:bg-cream/50 transition-colors"
                  >
                    <div className={`p-2 rounded-lg ${typeConfig.color} bg-opacity-20 flex-shrink-0`}>
                      <TypeIcon size={16} className={typeConfig.color} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-charcoal">{item.user_name}</span>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${statusConfig.color}`}>
                          <StatusIcon size={10} />
                          {statusConfig.label}
                        </span>
                        {item.image_url && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-indigo-50 text-indigo-600">
                            <ImageIcon size={10} />
                            Image
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-soft mt-1 line-clamp-2">{item.message}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-light">
                        <span>{item.user_email}</span>
                        <span>{new Date(item.created_at).toLocaleDateString()}</span>
                        {item.page_context && <span className="font-mono">{item.page_context}</span>}
                      </div>
                    </div>
                    <ChevronDown size={16} className={`text-gray-light transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div className="border-t border-beige p-4 bg-cream/30 space-y-4">
                      {/* Full message */}
                      <div>
                        <label className="text-xs font-medium text-charcoal uppercase tracking-wide">Full Message</label>
                        <p className="text-sm text-charcoal mt-1 whitespace-pre-wrap">{item.message}</p>
                      </div>

                      {/* Attached image */}
                      {item.image_url && (
                        <div>
                          <label className="text-xs font-medium text-charcoal uppercase tracking-wide mb-2 block">Attached Image</label>
                          <img
                            src={resolveImageUrl(item.image_url)}
                            alt="Feedback attachment"
                            className="max-h-48 rounded-lg border border-beige cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => setPreviewImage(resolveImageUrl(item.image_url))}
                          />
                        </div>
                      )}

                      {/* Admin notes */}
                      <div>
                        <label className="text-xs font-medium text-charcoal uppercase tracking-wide">Admin Notes</label>
                        <textarea
                          defaultValue={item.admin_notes || ''}
                          onBlur={(e) => {
                            if (e.target.value !== (item.admin_notes || '')) {
                              updateStatus(item.feedback_id, item.status, e.target.value);
                            }
                          }}
                          rows={2}
                          className="w-full mt-1 px-3 py-2 border border-beige rounded-lg text-sm focus:border-sage focus:ring-1 focus:ring-sage/20 resize-none"
                          placeholder="Add internal notes..."
                          data-testid={`admin-notes-${item.feedback_id}`}
                        />
                      </div>

                      {/* Status actions */}
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                          <button
                            key={key}
                            onClick={() => updateStatus(item.feedback_id, key)}
                            disabled={updatingId === item.feedback_id || item.status === key}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                              item.status === key
                                ? 'bg-charcoal text-white'
                                : `${cfg.color} hover:opacity-80 disabled:opacity-50`
                            }`}
                            data-testid={`set-status-${key}-${item.feedback_id}`}
                          >
                            {cfg.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Image preview lightbox */}
      {previewImage && (
        <div
          className="fixed inset-0 bg-charcoal/80 flex items-center justify-center z-[80] p-4 cursor-pointer"
          onClick={() => setPreviewImage(null)}
        >
          <img src={previewImage} alt="Preview" className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl" />
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminFeedback;
